import React, { useState, useEffect, useCallback } from 'react';
import { useClient } from '../../ClientContext';
import { reportingPost, AuthError } from '../../api';
import { gbp, num, pct } from '../../reporting/metrics';
import './reporting.css';



/**
 * Business context is stored per client, in the browser.
 *
 * It belongs on the server — it is client data, it should survive a machine
 * change, and once several people share an account they should share the
 * context too. For now it lives here, which is enough to demonstrate what it
 * does to a recommendation.
 */
const contextKey = (clientId) => `bidsense.businessContext.${clientId}`;

const CONTEXT_FIELDS = [
  {
    key: 'priorities',
    label: 'Commercial priorities',
    placeholder:
      'What matters this quarter, and why. e.g. "Launching the new estate in September — volume ' +
      'matters more than efficiency until October."',
  },
  {
    key: 'focus',
    label: 'Products or services to prioritise',
    placeholder:
      'What to push, and what to hold back. e.g. "Push the hybrid range. The petrol saloon is ' +
      'supply constrained until Q1 — do not scale it even if it performs."',
  },
  {
    key: 'constraints',
    label: 'Constraints and commitments',
    placeholder:
      'Anything that limits what can change. e.g. "Committed to a minimum share of voice in ' +
      'Scotland. Compliance requires the finance disclaimer on every asset."',
  },
  {
    key: 'measurement',
    label: 'How success is judged',
    placeholder:
      'e.g. "Lead quality matters more than volume — a lead that books a test drive is worth ten ' +
      'that do not. Consideration cycle is 6-8 weeks, so same-week CPL is misleading."',
  },
];

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable. The session still works.
  }
}



function BusinessContext({ value, onChange, onSave, saved }) {
  const [open, setOpen] = useState(false);
  const filled = CONTEXT_FIELDS.filter((f) => (value[f.key] || '').trim()).length;

  return (
    <div className="card ob-context">
      <button className="ob-context-head" onClick={() => setOpen(!open)}>
        <div>
          <div className="ob-context-title">Business context</div>
          <div className="ob-context-sub">
            {filled === 0
              ? 'Not set — recommendations will be generic until this is filled in'
              : `${filled} of ${CONTEXT_FIELDS.length} sections completed`}
          </div>
        </div>
        <span className="ob-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="ob-context-body">
          <p className="ob-context-intro">
            The same figures mean different things to different advertisers. A model that is
            selling out does not need more budget; an underperforming one you are contractually
            committed to cannot simply be paused. What you put here changes the recommendations
            rather than decorating them.
          </p>

          {CONTEXT_FIELDS.map((f) => (
            <div className="form-group" key={f.key}>
              <label className="form-label">{f.label}</label>
              <textarea
                className="form-input ob-textarea"
                value={value[f.key] || ''}
                onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                rows={3}
                maxLength={1000}
              />
            </div>
          ))}

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <button className="btn btn-brass" onClick={onSave}>
              {saved ? 'Saved' : 'Save'}
            </button>
            <span className="ob-context-note">
              Stored in this browser for now. Moves to your account when reporting goes live.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


const RANGES = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

const SEVERITY = {
  high:   { cls: 'blocked', label: 'High' },
  medium: { cls: 'flagged', label: 'Medium' },
  low:    { cls: 'observe', label: 'Low' },
};

/** Concatenate the context fields into the prose the model reads. */
function buildContextString(ctx) {
  return CONTEXT_FIELDS
    .filter((f) => (ctx[f.key] || '').trim())
    .map((f) => `${f.label}: ${ctx[f.key].trim()}`)
    .join('\n\n');
}

/**
 * The figures behind a finding.
 *
 * Collapsed behind a summary, because a media buyer's first question about
 * any recommendation is "on what basis" and their second is not. A tool that
 * cannot answer the first gets ignored after the second wrong call; one that
 * answers it in the reader's face every time gets skimmed.
 *
 * CLASS NAMES TAKEN FROM THE STYLESHEET, NOT FROM MEMORY. The first version
 * of this invented ob-evidence-item, ob-evidence-k and ob-evidence-v, none of
 * which exist, so the spans rendered unstyled and inline and the evidence
 * came out as "metriccpavalue£112.63account median£28.03worse by pct302%".
 */
function Evidence({ evidence }) {
  const entries = Object.entries(evidence || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object'
  );
  if (!entries.length) return null;

  const format = (k, v) => {
    if (typeof v !== 'number') return String(v);
    if (k.endsWith('_pct')) return `${v.toFixed(0)}%`;
    if (k.includes('ctr') || k.includes('cvr')) return pct(v);
    if (k.includes('cpa') || k.includes('spend') || k.includes('median')
        || k === 'value') return gbp(v, 2);
    return Number.isInteger(v) ? num(v) : v.toFixed(2);
  };

  // Spelled out rather than de-underscored, because "worse by pct" and
  // "metric cpa" are not English and this sits under a figure a client is
  // being asked to act on.
  const LABELS = {
    metric: 'Measured on',
    value: 'This ad set',
    account_median: 'Account median',
    worse_by_pct: 'Worse than typical by',
    better_by_pct: 'More efficient by',
    spend: 'Spend in window',
    conversions: 'Conversions',
    ctr_early: 'Click-through, first half',
    ctr_late: 'Click-through, second half',
    decline_pct: 'Decline',
    impressions: 'Impressions',
    conversions_per_week: 'Conversions a week',
    learning_threshold: 'Needed to leave learning',
    adsets_in_campaign: 'Ad sets in campaign',
    cpa_current: 'Cost per conversion, this period',
    cpa_previous: 'Cost per conversion, previous',
    change_pct: 'Change',
    spend_current: 'Spend, this period',
    spend_previous: 'Spend, previous',
  };
  const label = (k) =>
    LABELS[k] || k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

  return (
    <details className="rp-provenance ob-evidence">
      <summary>The figures behind this</summary>
      <div className="ob-evidence-grid">
        {entries.map(([k, v]) => (
          <div className="ob-ev" key={k}>
            <span className="ob-ev-k">{label(k)}</span>
            <span className="ob-ev-v">{format(k, v)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

function Finding({ finding, note, categories }) {
  const sev = SEVERITY[finding.severity] || SEVERITY.low;
  const entity = finding.entity || {};

  // WHICH AD SET, IN WHICH CAMPAIGN.
  //
  // The campaign name alone sat under the title, so two findings about two
  // different ad sets in one campaign looked like the same finding twice -
  // the title carried the only thing distinguishing them and the line beneath
  // repeated what they had in common.
  const where = entity.type === 'adset' && entity.campaign
    ? `Ad set in ${entity.campaign}`
    : entity.type === 'ad' && entity.adset
      ? `Ad in ${entity.adset}${entity.campaign ? ` · ${entity.campaign}` : ''}`
      : entity.campaign || null;

  return (
    <div className="card ob-finding">
      <div className="ob-finding-head">
        <span className={`pill ${sev.cls}`}>{sev.label}</span>
        <span className="tag-mono">
          {categories?.[finding.category] || finding.category}
        </span>
        {where && <span className="ob-entity">{where}</span>}
      </div>

      <h3 className="ob-title">{finding.title}</h3>
      <p className="ob-detail">{finding.detail}</p>

      {/* Where the client's context changes the reading. Set apart from the
          computed detail above, because one is arithmetic and the other is
          judgement, and a buyer should be able to tell which is which. */}
      {note && (
        <div className="ob-note">
          <span className="ob-note-k">In your context</span>
          {note}
        </div>
      )}

      <div className="ob-action">
        <span className="ob-action-k">Recommended</span>
        {finding.action}
      </div>

      <Evidence evidence={finding.evidence} />
    </div>
  );
}


export default function OptiBid() {
  const { selectedId, selected, clients, isAll } = useClient();

  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [context, setContext] = useState(() => loadJSON(contextKey(selectedId), {}));
  const [contextSaved, setContextSaved] = useState(false);

  useEffect(() => {
    setContext(loadJSON(contextKey(selectedId), {}));
  }, [selectedId]);

  // ONE REQUEST. The findings and the briefing come back together, computed
  // from the warehouse by the service — which is the whole reason this page
  // was rewritten. It used to generate its own data here.
  const run = useCallback(async (contextOverride) => {
    if (isAll || !selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await reportingPost('/reporting/optibid', {
        client_id: selectedId,
        days,
        business_context: buildContextString(contextOverride ?? context),
      });
      setData(result);
    } catch (err) {
      setError(err instanceof AuthError
        ? err.message
        : (err.message || 'Could not analyse this account.'));
      setData(null);
    }
    setBusy(false);
  }, [selectedId, isAll, days, context]);

  useEffect(() => { run(); }, [selectedId, days]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveContext = () => {
    saveJSON(contextKey(selectedId), context);
    setContextSaved(true);
    setTimeout(() => setContextSaved(false), 2000);
    run(context);
  };

  const findings = data?.findings || [];
  const noteFor = (id) =>
    (data?.brief?.notes || []).find((n) => n.finding_id === id)?.note || null;

  // The model's ordering, applied to findings it cannot add to. Anything it
  // left out keeps the computed order, after the ones it ranked.
  const ordered = (() => {
    const order = data?.brief?.priority_order || [];
    if (!order.length) return findings;
    const rank = new Map(order.map((id, i) => [id, i]));
    return [...findings].sort(
      (a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999)
    );
  })();

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 className="page-title">Opti-Bid</h1>
          <p className="page-subtitle">
            {isAll ? `All clients (${clients.length})` : (selected?.name || 'Client')} · findings
            computed from the warehouse, read against your commercial priorities
          </p>
        </div>
        <div className="rp-segmented" role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <button key={r.days} aria-pressed={days === r.days} onClick={() => setDays(r.days)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <BusinessContext
        value={context}
        onChange={setContext}
        onSave={saveContext}
        saved={contextSaved}
      />

      {isAll && (
        <div className="card">
          <div className="empty-state">
            <h3>Select a client</h3>
            <p>Opti-Bid analyses one advertiser's account at a time.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="card" style={{ marginBottom: 'var(--space-5)', padding: '12px 20px', borderLeft: '3px solid var(--red)' }}>
          <div style={{ fontSize: 13, color: 'var(--red)', fontWeight: 600 }}>{error}</div>
        </div>
      )}

      {busy && (
        <div className="card" style={{ padding: '20px 22px', marginBottom: 'var(--space-5)' }}>
          <div className="rp-thinking">
            <span className="rp-pulse" />
            Analysing the last {days} days…
          </div>
        </div>
      )}

      {!busy && data?.brief?.summary && (
        <div className="card ob-brief">
          <div className="ob-brief-k">This period</div>
          <p className="ob-brief-text">{data.brief.summary}</p>
        </div>
      )}

      {!busy && data && findings.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <h3>Nothing to act on</h3>
            <p>
              No cost, creative or structural issue crosses a threshold worth raising
              over the last {days} days.
            </p>
          </div>
        </div>
      )}

      {!busy && ordered.length > 0 && (
        <div className="ob-section-head">
          {ordered.length} recommendation{ordered.length === 1 ? '' : 's'}
        </div>
      )}

      {!busy && ordered.map((f) => (
        <Finding
          key={f.id}
          finding={f}
          note={noteFor(f.id)}
          categories={data?.category_labels}
        />
      ))}

      {(data?.caveats || []).length > 0 && (
        <div className="card" style={{ marginTop: 'var(--space-5)', padding: '14px 22px' }}>
          {data.caveats.map((c, i) => (
            <p key={i} style={{ margin: '4px 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>{c}</p>
          ))}
        </div>
      )}

      {/* NOT RUN, AND WHY. Four checks of eight read this account; a page
          showing four and not saying so looks like a page showing all of
          them, and the first question a prospect asks about an optimiser is
          what else it looks at. */}
      {!busy && (data?.unavailable || []).length > 0 && (
        <>
          <div className="ob-section-head">
            Not yet running — {data.unavailable.length} checks
          </div>
          <div className="card" style={{ padding: '4px 22px' }}>
            {data.unavailable.map((c) => (
              <div key={c.name} style={{ padding: '14px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.needs}</div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                  {c.why}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="rp-disclaimer">
        <strong>Account data.</strong> Every figure is computed from this client's
        warehouse by the reporting service. The findings are arithmetic over those
        figures; the model orders them and says what they mean for this client, and
        cannot add a finding or change a number in one.
      </p>
    </>
  );
}
